import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, ApiError, type Brief, type Challenge, type OrderDetail } from '../shared/api';
import { RiskPill, useToast } from '../shared/ui';

/**
 * The approval decision.
 *
 * Two things make this different from a normal "approve" button:
 *  - the decision carries `seen_payload_hash`, so it can only ever apply to
 *    the exact figures rendered on this screen;
 *  - the verification code is generated and checked on the server.
 */
export function ApprovalPanel({
  order, onChanged,
}: { order: OrderDetail; onChanged: () => void }) {
  const toast = useToast();
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [code, setCode] = useState('');
  const [comment, setComment] = useState('');
  const [rejecting, setRejecting] = useState(false);

  const { data: brief, isLoading: briefLoading } = useQuery({
    queryKey: ['brief', order.id],
    queryFn: () => api.get<Brief>(`/approvals/${order.id}/brief`),
    enabled: order.state === 'IN_APPROVAL',
    staleTime: 60_000,
  });

  const request = useMutation({
    mutationFn: () => api.post<Challenge>(`/approvals/${order.id}/challenge`),
    onSuccess: (c) => {
      setChallenge(c);
      toast(
        c.dev_code
          ? `Code sent to ${c.masked_destination}. Development code: ${c.dev_code}`
          : `Code sent to ${c.masked_destination}`,
        'ok',
      );
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Could not send a code', 'bad'),
  });

  const decide = useMutation({
    mutationFn: (approve: boolean) =>
      api.post(`/approvals/${order.id}/decide`, {
        approve,
        code: approve ? code : null,
        seen_payload_hash: order.payload_hash,
        comment: comment || null,
      }),
    onSuccess: (_, approve) => {
      setChallenge(null);
      setCode('');
      setComment('');
      setRejecting(false);
      onChanged();
      toast(approve ? 'Approved' : 'Rejected', approve ? 'ok' : 'bad');
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === 'stale_payload') {
        onChanged();
        toast('This order changed while you were reviewing it. Check the differences and decide again.', 'bad');
        setChallenge(null);
        return;
      }
      toast(e instanceof Error ? e.message : 'Decision failed', 'bad');
    },
  });

  if (order.state !== 'IN_APPROVAL') return null;

  if (!order.can_approve) {
    return (
      <div className="card">
        <div className="card-head"><div className="card-title">Your decision</div></div>
        <div className="alert alert-warn">
          {order.approval_block_reason ?? 'You cannot approve this payment order.'}
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{ borderColor: 'var(--seal-edge)' }}>
      <div className="card-head">
        <div className="card-title" style={{ color: 'var(--seal)' }}>Your decision</div>
        {brief && (
          <span className="ghost" style={{ fontSize: 10.5 }}>
            {brief.generated_by === 'llm' ? 'Briefed by AI' : 'Briefed from records'}
          </span>
        )}
      </div>

      {/* Copilot brief: context, never a recommendation. */}
      {briefLoading ? (
        <div className="skel" style={{ height: 90, marginBottom: 14 }} />
      ) : brief ? (
        <div
          style={{
            background: 'var(--ink)', borderRadius: 10, padding: 14, marginBottom: 14,
          }}
        >
          <div style={{ fontSize: 13.5, fontWeight: 500, lineHeight: 1.5 }}>{brief.summary}</div>
          <div className="faint" style={{ fontSize: 12.5, marginTop: 7 }}>{brief.context}</div>

          {brief.watch_items.length > 0 && (
            <>
              <div className="label" style={{ marginTop: 13, marginBottom: 6 }}>
                Worth checking
              </div>
              <ul style={{ margin: 0, paddingLeft: 17 }}>
                {brief.watch_items.map((w, i) => (
                  <li key={i} className="faint" style={{ fontSize: 12.5, marginBottom: 4, lineHeight: 1.5 }}>
                    {w}
                  </li>
                ))}
              </ul>
            </>
          )}

          {brief.risk.count > 0 && (
            <div className="row gap-6" style={{ marginTop: 12 }}>
              <RiskPill level={brief.risk.level} count={brief.risk.count} />
              <span className="ghost" style={{ fontSize: 11.5 }}>
                See the findings below before deciding
              </span>
            </div>
          )}
        </div>
      ) : null}

      {rejecting ? (
        <div className="col gap-10">
          <div className="field">
            <label className="label" htmlFor="reject-reason">Why are you rejecting this?</label>
            <input
              id="reject-reason"
              className="input"
              placeholder="The maker will see this"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              autoFocus
            />
          </div>
          <div className="row gap-6">
            <button className="btn btn-ghost grow" onClick={() => setRejecting(false)}>
              Back
            </button>
            <button
              className="btn btn-bad grow"
              disabled={decide.isPending || !comment}
              onClick={() => decide.mutate(false)}
            >
              {decide.isPending ? 'Rejecting…' : 'Reject order'}
            </button>
          </div>
        </div>
      ) : !challenge ? (
        <div className="col gap-10">
          <div className="faint" style={{ fontSize: 12.5, lineHeight: 1.55 }}>
            Approving sends a verification code to your registered email. The code is
            generated and checked on the server — it is never handled by this browser.
          </div>
          <div className="row gap-6">
            <button
              className="btn btn-primary grow"
              disabled={request.isPending}
              onClick={() => request.mutate()}
            >
              {request.isPending ? 'Sending…' : 'Approve — send code'}
            </button>
            <button className="btn btn-bad" onClick={() => setRejecting(true)}>
              Reject
            </button>
          </div>
        </div>
      ) : (
        <div className="col gap-10">
          <div className="field">
            <label className="label" htmlFor="otp">
              Code sent to {challenge.masked_destination}
            </label>
            <input
              id="otp"
              className="input otp-input"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="······"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => { if (e.key === 'Enter' && code.length === 6) decide.mutate(true); }}
              autoFocus
            />
          </div>

          <div className="field">
            <label className="label" htmlFor="approve-comment">Comment (optional)</label>
            <input
              id="approve-comment"
              className="input"
              placeholder="Recorded in the audit trail"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </div>

          <div className="row gap-6">
            <button
              className="btn btn-ghost"
              onClick={() => { setChallenge(null); setCode(''); }}
            >
              Cancel
            </button>
            <button
              className="btn btn-ok grow"
              disabled={code.length !== 6 || decide.isPending}
              onClick={() => decide.mutate(true)}
            >
              {decide.isPending ? 'Approving…' : 'Confirm approval'}
            </button>
          </div>

          <div className="ghost" style={{ fontSize: 11, textAlign: 'center' }}>
            Your approval will be sealed against digest{' '}
            <span className="mono" style={{ color: 'var(--seal)' }}>
              {order.payload_hash.slice(0, 8)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
